import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { IconButton, Tooltip, Typography } from '@mui/material';
import { httpRequest } from 'src/services/httpRequest';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import toast from 'react-hot-toast';
import { handleError } from 'src/redux-store/slices/errorSlice';
import { useDispatch } from 'react-redux';
import { Box } from '@mui/system';
import VisibilityIcon from '@mui/icons-material/Visibility';
import OfflinePinIcon from '@mui/icons-material/OfflinePin';
import CancelIcon from '@mui/icons-material/Cancel';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import getEnvVars from 'src/config/env';
import {
  MAX_UPLOAD_FILE_SIZE_BYTES,
  MAX_UPLOAD_FILE_SIZE_ERROR
} from 'src/constants/uploadLimits';

export default function UploadDocs({
  file,
  reFetch,
  setFile,
  setLoading,
  permitName,
  ApplicationID,
  documentToUpload = [],
  staticType = []
}) {
  const dispatch = useDispatch();
  const { permit } = useParams();
  const [documentTypes, setDocumentTypes] = useState([]);
  const [formateError, setFormateError] = useState<string>('');

  // This function handles the upload of the document to the server
  const handleUploadDocument = async (fileName: string, rowName: string) => {
    if (ApplicationID) {
      setLoading(true);
      const reqObj = {
        ApplicationID: ApplicationID,
        PermitID: permit,
        DocumentFileUpload: fileName,
        DocumentType: rowName
      };
      const url = `/uploadPDF/insert_PDF`;
      const response = await httpRequest('POST', url, reqObj);
      if (response?.status === 'success') {
        toast.success('Document uploaded successfully.');
        reFetch();
      } else {
        toast.error(response?.message);
        dispatch(handleError(response?.message));
      }
      setLoading(false);
    }
  };

  // This function handles the file upload when a user selects a file
  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    rowName: string
  ) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;
    const newFileName = `${selectedFile?.name}`;
    const modifiedFile = new File([selectedFile], newFileName, {
      type: selectedFile.type
    });

    const selectedFiles = Array.from(event.target.files || []);
    const allowedImageTypes = [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/svg+xml',
      'image/heic'
    ];
    const allowedPdfType = 'application/pdf';

    const validFiles = selectedFiles.filter((fileItem) => {
      const isValidImage =
        allowedImageTypes.includes(fileItem.type) ||
        fileItem.name.toLowerCase().endsWith('.heic');
      const isValidPdf = fileItem.type === allowedPdfType;
      const isValidSize = fileItem.size <= MAX_UPLOAD_FILE_SIZE_BYTES;
      return isValidSize && (isValidImage || isValidPdf);
    });

    if (validFiles.length === 0 && selectedFiles.length > 0) {
      const sizeError = selectedFiles.some(
        (fileItem) => fileItem.size > MAX_UPLOAD_FILE_SIZE_BYTES
      );
      setFormateError(
        sizeError
          ? MAX_UPLOAD_FILE_SIZE_ERROR
          : 'Please upload image file (JPG, PNG, GIF, SVG, HEIC) or PDF file only.'
      );
    } else {
      setFormateError('');
      setLoading(true);
      const formData = new FormData();
      formData.append('uploadPDF', modifiedFile);
      const url = `/uploadPDF/?permitName=${permitName}`;
      try {
        const response = await httpRequest('POST', url, formData, {
          'Content-Type': 'multipart/form-data'
        });

        if (response?.status === 'success') {
          const uploadFileName = response?.data?.fileName;
          const downloadURL = response?.data?.downloadURL;
          if (ApplicationID) {
            handleUploadDocument(uploadFileName, rowName);
          }

          if (ApplicationID) {
            setFile((prevFile) => [
              ...prevFile,
              {
                Type: rowName,
                FileName: uploadFileName,
                downloadURL: downloadURL
              }
            ]);
          } else {
            setFile((prevFile) => [
              ...prevFile,
              {
                Type: rowName,
                FileName: uploadFileName
              }
            ]);
          }
        } else {
          toast.error(response?.message);
          dispatch(handleError(response?.message));
          setLoading(false);
        }
      } catch (error) {
        console.error('Upload failed', error);
        toast.error('File upload failed.');
        setLoading(false);
      } finally {
        event.target.value = '';
        if (!ApplicationID) {
          setLoading(false);
        }
      }
    }
  };

  const rows = documentToUpload?.length ? documentToUpload : documentTypes;

  //map rows to get the document types
  const handleUploadedFiles = (name: string) => {
    if (file.length > 0) {
      const fileNames = file?.map((val) => val?.Type);
      return fileNames?.includes(name);
    } else return false;
  };

  // Helper function to get full URL for document preview
  const getFullUrl = (url: string): string => {
    // If URL is already absolute (starts with http), return as-is
    if (url && url.startsWith('http')) {
      return url;
    }
    const { socketUrl } = getEnvVars();
    // If URL is relative, prepend the backend server URL
    const backendUrl = socketUrl || 'http://localhost:5000';
    return `${backendUrl}${url}`;
  };

  const handleUploadeddownloadURL = (name: string) => {
    const fileNames = file?.filter((val) => val?.Type === name);
    const downloadURL = fileNames[0]?.downloadURL;
    if (!downloadURL) return '#';
    return getFullUrl(downloadURL);
  };

  const handleDeleteDocumennt = async (name) => {
    const fileNames = file?.filter((val) => val?.Type === name);
    const ApplicationDocsID = fileNames[0].ApplicationDocsID;

    setLoading(true);
    const reqObj = {
      ApplicationID: ApplicationID,
      PermitID: permit
    };
    const response = await httpRequest(
      'DELETE',
      `/uploadPDF?ApplicationDocsID=${ApplicationDocsID}`,
      reqObj
    );
    if (response?.status === 'success') {
      toast.success('Document deleted successfully.');
      reFetch();
    } else {
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
    setLoading(false);
  };

  // This function fetches the document types from the server
  async function getDocumentType() {
    try {
      const response = await httpRequest(
        'GET',
        `/configuration/getDocumentTypes?PermitID=${permit}`,
        { 'Content-Type': 'multipart/form-data' }
      );

      if (response?.status === 'success') {
        if (response?.data?.length) {
          setDocumentTypes(response?.data);
        } else {
          setDocumentTypes(staticType);
        }
      } else {
        toast.error(response?.message);
        dispatch(handleError(response?.message));
      }
    } catch (error) {
      console.error('Upload failed', error);
      toast.error('File upload failed.');
    }
  }

  useEffect(() => {
    getDocumentType();
  }, []);

  return (
    <Box flexDirection={'column'}>
      <Typography mb={1}>
        {/* Only PDF files are allowed,
        with maximum file size of 10MB. */}
        Only image files (JPG, PNG, GIF, SVG, HEIC) and PDF files are allowed, with maximum file size of 10MB.
      </Typography>
      <Typography pb={1} color="rgb(167, 42, 42)">
        {formateError}
      </Typography>
      <TableContainer component={Paper}>
        <Table
          sx={{ minWidth: { xs: 'auto', sm: 500 } }}
          aria-label="caption table"
        >
          <TableBody>
            {rows?.map((rowName) => (
              <TableRow key={rowName}>
                <TableCell component="th" scope="row">
                  {rowName}
                </TableCell>
                {handleUploadedFiles(rowName) ? (
                  <>
                    <TableCell align="right">
                      <Tooltip title="Uploaded File" arrow>
                        <label htmlFor={`file-upload-${rowName}`}>
                          <IconButton
                            component="span"
                            size="large"
                            color="primary"
                          >
                            <OfflinePinIcon sx={{ color: 'green' }} />
                          </IconButton>
                        </label>
                      </Tooltip>

                      {ApplicationID && (
                        <Tooltip title="Delete File" arrow>
                          <IconButton
                            component="span"
                            size="large"
                            color="error"
                            onClick={() => handleDeleteDocumennt(rowName)}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {ApplicationID && (
                        <Tooltip title="View" arrow>
                          <IconButton
                            color="info"
                            size="large"
                            href={handleUploadeddownloadURL(rowName)}
                            target="_blank"
                          >
                            <VisibilityIcon fontSize="medium" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </>
                ) : (
                  <TableCell align="right">
                    <Tooltip title="Upload File">
                      <Box>
                        <input
                          accept="application/pdf"
                          type="file"
                          onChange={(event) => handleFileChange(event, rowName)}
                          style={{ display: 'none' }}
                          id={`file-upload-${rowName}`}
                        />
                        <label htmlFor={`file-upload-${rowName}`}>
                          <IconButton
                            component="span"
                            size="large"
                            color="primary"
                          >
                            <CloudUploadIcon />
                          </IconButton>
                        </label>
                      </Box>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
